import { BankOutlined as BankIcon } from "@ant-design/icons"
import { BankConnection, DocumentTypeEnum, Employee, Expense, ExpensePaymentFlowInformationEnum, ExpenseSplit, ExpenseStatusEnum } from "@finway-group/shared/lib/models"
import { ExpensePaymentOptionEnum } from "@finway-group/shared/lib/models/expense/expensePaymentOption.enum"
import { Button, Divider, Dropdown, Menu, Tooltip, Upload } from "antd"
import React from "react"
import { Bell as BellIcon, X as CancelIcon, Check as CheckIcon, Settings as CogIcon, MoreHorizontal as MoreIcon, Upload as UploadIcon } from "react-feather"
import { Trans, useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { Link } from "react-router-dom"

import { ConditionalWrapper } from "Components/conditionalWrapper"
import ExportDropDownMenu from "Components/exportDropDownMenu"
import SEPAExportConfirmationModal from "Components/modals/sepaExportConfirmation.modal"
import { ExpenseExport } from "Features/export/expenseExport"
import { API_URL } from "Shared/config/consts"
import { useModal } from "Shared/context/modal.context"
import { useInboxInvoices } from "Shared/hooks/expense.hooks"
import { useCreditorById, useVendorById } from "Shared/hooks/vendor.hooks"
import { approveVendorStatus, deleteVendor } from "Shared/store/actions/vendor/vendorActions"
import { RootState } from "Shared/store/rootState.interface"
import breakpoints from "Shared/utils/breakpoints"
import { getTooltipPopupContainer } from "Shared/utils/helpers"
import { useResizeWindowWidth } from "Shared/utils/hooks/useResizeWindowWidth"
import useStateIfMounted from "Shared/utils/hooks/useStateIfMounted"

import { exportSepa, isExpenseAbleToProceed, isMileageOrPerDiem } from "../expenseUtils"
import ExpenseCostCenterBudgetUsageProgressBar from "./expenseCostCenterBudgetUsageProgressBar"

interface ExpenseActionButtonViewInterface {
    expense: Expense
    loggedInUser: Employee
    isSubscription: boolean
    isLoading: boolean
    isInvoiceUploading: boolean
    rejectRequest: () => void
    markRequestAsApproved: (expense: Expense, userId: string) => void
    markRequestAsReviewed: () => void
    markRequestAsPaid: () => void
    payExpense: (isScheduledPayment: boolean) => void
    doFileUpload: (file: any, kind: DocumentTypeEnum) => void
    onInboxSelected: (event: any) => void
    isButtonDisabled?: boolean
    isExpenseUpdating?: boolean
    isOcrUploading: boolean
    sendEmailReminder: () => void
}

const ExpenseActionButtonView: React.FC<ExpenseActionButtonViewInterface> = ({
    expense,
    loggedInUser,
    isLoading,
    isInvoiceUploading,
    rejectRequest,
    markRequestAsApproved,
    markRequestAsReviewed,
    markRequestAsPaid,
    payExpense,
    doFileUpload,
    onInboxSelected,
    isButtonDisabled,
    isExpenseUpdating,
    isOcrUploading,
    sendEmailReminder,
}) => {
    const { Dragger } = Upload

    const { t } = useTranslation()
    const hasDefaultBankConnection = useSelector(({ banks }: RootState) => banks.items)?.find((con: BankConnection) => con.isDefault)
    const isRefundExpense = expense.totalNetPrice < 0 || expense.totalGrossPrice < 0
    const shouldShowPayNow = hasDefaultBankConnection && !isRefundExpense
    const windowWidth = useResizeWindowWidth()
    const creditor = useCreditorById(expense.vendor)
    const isApprover =
        (expense.approvalNeededBy === loggedInUser.id || expense.secondApprovalNeededBy === loggedInUser.id) && !expense.approvalsForActiveWorkflowRule.includes(loggedInUser.id)
    const isRequester = expense.requestedBy === loggedInUser.id
    const inboxInvoiceData = useInboxInvoices()
    const dispatch = useDispatch()
    const { showModal } = useModal()
    const vendor = useVendorById(expense.vendor)
    const [isUpdatingVendorStatus, setIsUpdatingVendorStatus] = useStateIfMounted(false)

    const approveOrRejectVendor = async (isVendorApproved: boolean) => {
        setIsUpdatingVendorStatus(true)
        try {
            if (isVendorApproved) {
                await dispatch(approveVendorStatus(expense.vendor))
            } else {
                await dispatch(deleteVendor(expense.vendor))
            }
        } finally {
            setIsUpdatingVendorStatus(false)
        }
    }

    const renderApprovalConfirmationView = () => {
        return loggedInUser.activeCompanyProfile.isAdmin || isApprover ? (
            <>
                <h2 className="mb-0 pl-0 -mt-3">{t("label:approval_confirmation")}</h2>
                <p className="mb-5">{t("label:approval_confirmation_desc")}</p>
                <div className="btn-wrapper sm:flex-col lg:flex-row fixed left-0 right-0 bottom-0 py-16 px-10 bg-white shadow sm:relative sm:left-auto sm:right-auto sm:bottom-auto sm:p-0 sm:pt-12 sm:pb-2 sm:bg-transparent sm:shadow-none z-20 sm:z-0">
                    <Button className="sm:py-5 sm:px-10 flex-1 btn-default" onClick={rejectRequest}>
                        <CancelIcon />
                        <span>{t("action:reject")}</span>
                    </Button>
                    <Button
                        loading={isLoading}
                        className="sm:py-5 sm:px-10 flex-1 sm:mt-10 lg:mt-0 sm:ml-0 lg:ml-10"
                        onClick={() => markRequestAsApproved(expense, loggedInUser.id)}
                        type="primary"
                        disabled={isOcrUploading || isExpenseUpdating}
                    >
                        <CheckIcon />
                        <span>{t("action:approve")}</span>
                    </Button>
                </div>
                {expense.splits.filter((expenseSplit: ExpenseSplit) => expenseSplit.costCenter).length !== 0 && (
                    <>
                        <Divider></Divider>
                        <ExpenseCostCenterBudgetUsageProgressBar data={[...new Set(expense.splits.map((c) => c.costCenter ?? ""))]}></ExpenseCostCenterBudgetUsageProgressBar>
                    </>
                )}
            </>
        ) : (
            <>
                <h2 className="mb-0 pl-0 -mt-3">{t("label:approval_confirmation_non_approver")}</h2>
                <p className="mb-5">{t("label:approval_confirmation_desc_non_approver")}</p>
            </>
        )
    }

    const renderDocumentsNeededView = () => {
        return (loggedInUser.activeCompanyProfile.isAdmin && !isRequester) || isApprover ? (
            <>
                <div>
                    <h2 className="mb-0 pl-0 -mt-3">{t("label:missing_invoices")}</h2>
                    <p className="mb-5">{t("label:missing_invoices_desc")}</p>
                </div>
                <Button className="flex-1 btn-highlight-blue m-2 p-5 w-full" onClick={sendEmailReminder}>
                    <BellIcon />
                    <span className="m-0">{t("action:send_reminder")}</span>
                </Button>
            </>
        ) : (
            <>
                <>
                    <div>
                        <h2 className="mb-0 pl-0 -mt-3">{t("label:upload_documents")}</h2>
                        <p className="mb-5">{t("label:upload_documents_desc")}</p>
                    </div>
                    <Dragger
                        action={`${API_URL}/files`}
                        headers={{ Authorization: `Bearer ${localStorage.accessToken}` }}
                        multiple
                        accept=".jpg,.jpeg,.png,.pdf"
                        showUploadList={false}
                        customRequest={(req) => {
                            doFileUpload(req.file, DocumentTypeEnum.INVOICE)
                        }}
                        disabled={isInvoiceUploading}
                    >
                        {inboxInvoiceData.count > 0 && (
                            <Button className="btn-highlight-blue mt-10 mr-8" onClick={onInboxSelected}>
                                <UploadIcon />
                                {t("action:inbox_invoice.inbox")}
                            </Button>
                        )}
                        <Button loading={isInvoiceUploading} className="btn-highlight-blue mt-10">
                            <UploadIcon />
                            <span>{t("action:upload_invoice")}</span>
                        </Button>
                    </Dragger>
                </>
            </>
        )
    }

    const renderExpenseApprovedView = () => {
        const isButtonDisabled = !isExpenseAbleToProceed(expense, creditor) && expense.paymentFlowInformation !== ExpensePaymentFlowInformationEnum.REPORTING_ONLY

        const reviewDescription = isMileageOrPerDiem(expense) ? t("label:review_mileage_perdiem_desc") : t("label:review_confirmation_desc")

        return loggedInUser.activeCompanyProfile.isAdmin ? (
            (!vendor || vendor.isApproved ? (
                <>
                    <h2 className="mb-0 pl-0 -mt-3">{t("label:review_confirmation")}</h2>
                    <p className="mb-5">{reviewDescription}</p>
                    <div className="btn-wrapper sm:flex-col lg:flex-row fixed left-0 right-0 bottom-0 py-16 px-10 bg-white shadow sm:relative sm:left-auto sm:right-auto sm:bottom-auto sm:p-0 sm:pt-12 sm:pb-2 sm:bg-transparent sm:shadow-none z-20 sm:z-0 flex flex-wrap">
                        <Button className="sm:py-5 sm:px-10 flex-1 btn-default mx-2 my-5" onClick={rejectRequest}>
                            <CancelIcon />
                            <span>{t("action:reject")}</span>
                        </Button>
                        <ConditionalWrapper
                            condition={isButtonDisabled}
                            wrapper={(children: any) => (
                                <Tooltip
                                    getPopupContainer={getTooltipPopupContainer}
                                    title={t("tooltips:expenses.not_all_information_provided")}
                                    placement="bottom"
                                    className="sm:relative sm:flex-col flex flex-1 mt-0"
                                >
                                    {children}
                                </Tooltip>
                            )}
                        >
                            <Button
                                disabled={isButtonDisabled || isExpenseUpdating}
                                loading={isButtonDisabled ? false : isLoading}
                                className={isButtonDisabled ? "sm:py-5 flex-1 mx-2 my-5" : "ant-btn-primary sm:py-5 sm:px-10 flex-1 mx-2 my-5"}
                                type={isButtonDisabled ? "primary" : "default"}
                                onClick={markRequestAsReviewed}
                            >
                                <CheckIcon />
                                <span>{t("action:mark_as_reviewed")}</span>
                            </Button>
                        </ConditionalWrapper>
                    </div>
                </>
            ) : (
                <>
                    <h2 className="mb-0 pl-0 -mt-3">{t("label:vendor_confirmation")}</h2>
                    <p className="mb-5">
                         <Trans t={t} values={{vendorName: vendor.name}} i18nKey="label:vendor_confirmation_desc" components={[<Link key="linkKey" to={`/vendors/${vendor.id}`} />]} />
                    </p>
                    <div className="btn-wrapper sm:flex-col lg:flex-row fixed left-0 right-0 bottom-0 py-16 px-10 bg-white shadow sm:relative sm:left-auto sm:right-auto sm:bottom-auto sm:p-0 sm:pt-12 sm:pb-2 sm:bg-transparent sm:shadow-none z-20 sm:z-0 flex flex-wrap">
                        <Button className="sm:py-5 sm:px-10 flex-1 btn-default mx-2 my-5" onClick={() => approveOrRejectVendor(false)}>
                            <CancelIcon />
                            <span>{t("action:reject_vendor")}</span>
                        </Button>
                        <Button
                            disabled={isUpdatingVendorStatus}
                            loading={isUpdatingVendorStatus}
                            className={isUpdatingVendorStatus ? "sm:py-5 flex-1 mx-2 my-5" : "ant-btn-primary sm:py-5 sm:px-10 flex-1 mx-2 my-5"}
                            type={isUpdatingVendorStatus ? "primary" : "default"}
                            onClick={() => approveOrRejectVendor(true)}
                        >
                            <CheckIcon />
                            <span>{t("action:approve_vendor")}</span>
                        </Button>
                    </div>
                </>
            )
        ) : (
            <>
                <h2 className="mb-0 pl-0 -mt-3">{t("label:review_phase")}</h2>
                <p className="mb-5">{t("label:review_phase_desc")}</p>
            </>
        )
    }

    const renderReviewedExpenseView = () => {
        const onCmd = ({ key, domEvent }: any) => {
            domEvent.stopPropagation()
            switch (key) {
                case "mark_as_paid":
                    markRequestAsPaid()
                    break
                case "pay_on_date":
                    payExpense(true)
                    break
                case "generate_sepa":
                    showModal(SEPAExportConfirmationModal, true, {
                        isShowing: true,
                        expenses: [expense],
                        onSubmit: (expenses: Expense[], subjects: any) => exportSepa(expenses, subjects),
                    })
                    break
                default:
                    break
            }
        }

        const checkPlacement = () => {
            return windowWidth < breakpoints.sm ? "topRight" : "bottomLeft"
        }

        return expense.paymentOption === ExpensePaymentOptionEnum.SMART_CARD ? (
            <>
                <h2 className="mb-0 pl-0 -mt-3">{t("label:cards.waiting_for_card_transaction.title")}</h2>
                <p className="mb-5">{t("label:cards.waiting_for_card_transaction.message")}</p>
            </>
        ) : loggedInUser.activeCompanyProfile.isAdmin ? (
            <>
                <h2 className="mb-0 pl-0 -mt-3">{shouldShowPayNow ? t("label:pay_confirmation") : t("label:mark_confirmation")}</h2>
                <p className="mb-5">{shouldShowPayNow ? t("label:pay_confirmation_desc") : t("label:mark_confirmation_desc")}</p>
                <div className="btn-wrapper md:flex-col lg:flex-row fixed left-0 md:left-40 right-0 bottom-0 py-16 px-10 bg-white shadow sm:relative sm:left-auto sm:right-auto sm:bottom-auto sm:p-0 sm:pt-12 sm:pb-2 sm:bg-transparent sm:shadow-none z-20 sm:z-0">
                    <Button
                        loading={isLoading}
                        className="ant-btn-primary text-center  md:py-5 md:px-10 flex-1 btn-default"
                        onClick={shouldShowPayNow ? () => payExpense(false) : markRequestAsPaid}
                        disabled={expense.isInPaymentProcess || isExpenseUpdating}
                    >
                        <CheckIcon className="mr-2" />
                        <span>{shouldShowPayNow ? t("action:banking.pay_now") : t("action:mark_as_paid")}</span>
                    </Button>
                    <Dropdown
                        trigger={["click"]}
                        placement={checkPlacement()}
                        arrow
                        disabled={expense.isInPaymentProcess || isExpenseUpdating}
                        overlay={
                            <Menu onClick={onCmd}>
                                {shouldShowPayNow && (
                                    <>
                                        <Menu.Item key="pay_on_date" className="flex items-center min-w-190 py-8">
                                            <div className="flex items-center justify-center rounded-full w-16 h-16">
                                                <BankIcon />
                                            </div>
                                            <div className="flex items-center ml-12">
                                                <p className="leading-none">
                                                    <span className="text-text">{t("action:banking.pay_on_date")}</span>
                                                </p>
                                            </div>
                                        </Menu.Item>
                                        <Menu.Item key="mark_as_paid" className="flex items-center min-w-190 py-8">
                                            <div className="flex items-center justify-center rounded-full w-16 h-16">
                                                <CheckIcon />
                                            </div>
                                            <div className="flex items-center ml-12">
                                                <p className="leading-none">
                                                    <span className="text-text">{t("action:mark_as_paid")}</span>
                                                </p>
                                            </div>
                                        </Menu.Item>
                                    </>
                                )}
                                <Menu.Item key="generate_sepa" className="flex items-center min-w-190 py-8">
                                    <div className="flex items-center justify-center rounded-full w-16 h-16">
                                        <CogIcon />
                                    </div>
                                    <div className="flex items-center ml-12">
                                        <p className="leading-none">
                                            <span className="text-text">{t("action:generate_sepa")}</span>
                                        </p>
                                    </div>
                                </Menu.Item>
                            </Menu>
                        }
                    >
                        <Button className="md:py-5 md:px-10 md:mt-10 lg:mt-0 md:ml-0 lg:ml-10">
                            <MoreIcon />
                        </Button>
                    </Dropdown>
                </div>
            </>
        ) : (
            <>
                <h2 className="mb-0 pl-0 -mt-3">{t("label:pay_phase")}</h2>
                <p className="mb-5">{t("label:pay_phase_desc")}</p>
            </>
        )
    }

    const renderPaidExpenseView = () => {
        return loggedInUser.activeCompanyProfile.isAdmin ? (
            <>
                <h2 className="mb-0 pl-0 -mt-3">{t("label:export_confirmation")}</h2>
                <p className="mb-5">{t("label:export_confirmation_desc")}</p>
                <div className="w-full">
                    <ExpenseExport
                        render={({ onExport, isExporting }) => <ExportDropDownMenu onExport={onExport} expenses={[expense]} isLoading={isExporting} includeSepa primaryButton />}
                    />
                </div>
            </>
        ) : (
            <>
                <h2 className="mb-0 pl-0 -mt-3">{t("label:export_phase")}</h2>
                <p className="mb-5">{t("label:export_phase_desc")}</p>
            </>
        )
    }

    const renderDeletedStatus = () => {
        return (
            <>
                <h2 className="mb-0 pl-0 -mt-3">{t("label:expense_deleted")}</h2>
                <p className="mb-5">{t("label:expense_deleted_desc")}</p>
            </>
        )
    }

    const renderDeclinedExpenseView = () => {
        return (
            <>
                <h2 className="mb-0 pl-0 -mt-3">{t("label:expense_rejected")}</h2>
                <p className="mb-5">{t("label:expense_rejected_desc")}</p>
            </>
        )
    }

    const getActionHeader = () => {
        if (expense.deleted) return renderDeletedStatus()

        switch (expense.status) {
            case ExpenseStatusEnum.PURCHASE_PENDING:
            case ExpenseStatusEnum.INVOICE_PENDING:
                return renderApprovalConfirmationView()
            case ExpenseStatusEnum.DOCS_NEEDED:
                return renderDocumentsNeededView()
            case ExpenseStatusEnum.APPROVED:
                return renderExpenseApprovedView()
            case ExpenseStatusEnum.REVIEWED:
                return renderReviewedExpenseView()
            case ExpenseStatusEnum.PAID:
                return renderPaidExpenseView()
            case ExpenseStatusEnum.DECLINED:
                return renderDeclinedExpenseView()
            default:
                return (
                    <>
                        <h2 className="mb-0 pl-0 -mt-3 -mb-2">{t("label:past_request")}</h2>
                        <p className="mb-5">{t("label:past_request_desc")}</p>
                    </>
                )
        }
    }

    return getActionHeader()
}

export default ExpenseActionButtonView
